import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import ReactDOM from 'react-dom';

import { useComputeOffset } from '@features/ToastManager/lib/hooks/useComputeOffset';
import { subscribeToast } from '@features/ToastManager/lib/toastBus';
import {
    IRenderPropsArgs,
    IToastManagerProps,
    IToastPayload,
} from '@features/ToastManager/model/types';
import { PushNotification } from '@shared/ui';

export const ToastManager = ({
    toastPosition,
    pushStyles,
    containerStyles,
    delay = 3000,
    scope = 'global',
    portalTarget,
    strategy = 'fixed',
    renderToast,
}: IToastManagerProps) => {
    const [toast, setToast] = useState<IToastPayload | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { offset, setOffset, computeOffset } = useComputeOffset({ strategy, toastPosition });

    useLayoutEffect(() => {
        if (strategy !== 'fixed') return;
        const update = () => setOffset(computeOffset());
        update();

        window.addEventListener('resize', update);

        let mo: MutationObserver | null = null;
        if (toastPosition === 'left') {
            mo = new MutationObserver(() => update());
            mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        }

        return () => {
            window.removeEventListener('resize', update);
            if (mo) mo.disconnect();
        };
    }, [toastPosition, computeOffset, setOffset, strategy]);

    useLayoutEffect(() => {
        const unsub = subscribeToast((payload) => {
            if (payload.scope !== scope) return;

            setToast(payload);

            if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
            timeoutRef.current = window.setTimeout(() => setToast(null), payload.delay ?? delay);
        });

        return () => {
            unsub();
            if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
        };
    }, [scope, delay]);

    const inlineStyles = useMemo(() => {
        const base = toastPosition === 'left' ? { left: `${offset}px` } : { right: `${offset}px` };

        return strategy === 'absolute'
            ? { ...base, position: 'absolute' as const }
            : { ...base, position: 'fixed' as const };
    }, [toastPosition, offset, strategy]);

    const target = portalTarget ?? document.body;

    const renderToastArgs: IRenderPropsArgs = {
        isToastShow: !!toast,
        text: toast?.text,
        positionStyles: inlineStyles,
        type: toast?.type ?? 'success',
    };

    if (!toast) return null;

    return ReactDOM.createPortal(
        renderToast ? (
            renderToast(renderToastArgs)
        ) : (
            <PushNotification
                textContent={toast.text}
                isOpen={!!toast}
                inlineStyles={inlineStyles}
                pushStyles={pushStyles}
                containerStyles={containerStyles}
            />
        ),
        target
    );
};
